import React, { useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import Listbox from '../../components/Listbox';
import { iconOf, OfferIcon } from './offerIcons';
import { libelleUnite, cleUnite, HEURES_JOUR } from './offerUnits';
import './OfferCreateDialog.css';

/* ── Nouvelle offre / Modifier l'offre (2026-09-11) ───────────────────────
   Une seule surface, deux sections discrètes : le PRODUIT (comment on vend,
   comment on le planifie, nom, prix), puis les CONDITIONS que le catalogue
   propose et qu'on ajuste. Un résumé d'une ligne sous les conditions dit ce
   que le plan demandera ; il se déplie. Les listes sont des `Listbox` : le
   menu s'ouvre sous le champ, de sa largeur, et rien ne le coupe.
   Refonte compacte du 2026-09-11, sur les mêmes champs et les mêmes règles. */

export const PRINCIPAUX = ['produit', 'conseil', 'carnet', 'services_manages', 'projet', 'abonnement', 'formation', 'espace'];
const CHOIX = (p) => ({
  produit: { quest: p("produit physique, vendu à l'unité"), mode: p('Ponctuel') },
  volume: { quest: p('un volume consommé'), mode: p("À l'usage") },
  conseil: { quest: p('temps de prestations'), mode: p("À l'usage") },
  carnet: { quest: p("carnet d'heures"), mode: p('Ponctuel') },
  formation: { quest: p('formation'), mode: p('Ponctuel') },
  transport: { quest: p('du transport'), mode: p("À l'usage") },
  forfait: { quest: p('un service à la pièce'), mode: p('Ponctuel') },
  projet: { quest: p('projet'), mode: p('Ponctuel') },
  services_manages: { quest: p('service récurrent'), mode: p('Récurrent') },
  abonnement: { quest: p('abonnement & licence'), mode: p('Récurrent') },
  licence: { quest: p('une licence'), mode: p('Récurrent') },
  location: { quest: p('une location'), mode: p('Récurrent') },
  espace: { quest: p('capacité'), mode: p("À l'usage") },
  commission: { quest: p('une commission'), mode: p("À l'usage") },
  autre: { quest: p('un modèle personnalisé'), mode: p('à choisir') },
});
/* Pour du TEMPS, la question porte l'unité (2026-09-11) : en heures, en
   jours-hommes, en EPT, ou en valeur. Choisir l'une pose l'unité de l'offre. */
const PLAN_TEMPS = (p) => ([
  /* Du temps se planifie en heures ET en jours-hommes, qu'on alterne dans le
     plan (2026-09-11) : une seule réponse, qui ne se change pas. L'offre se
     garde en heures. */
  { value: 'time_rate:h', label: p('en heures ou en jours-hommes, à un tarif'), unit: 'h', saisie: p('des heures ou des jours-hommes par mois') },
]);
/* Une formation se compte par participant ou par session : même équation
   (quantité × prix), l'unité change (2026-09-11). */
const PLAN_FORMATION = (p) => ([
  { value: 'volume_price:participant', label: p('en nombre de participants, à un prix unitaire'), unit: 'participant', saisie: p('des participants par mois') },
  { value: 'volume_price:formation', label: p('en nombre de formations, à un prix unitaire'), unit: 'formation', saisie: p('des formations par mois') },
]);
/* Les modèles dont la question « Comment le planifiez-vous ? » pose aussi
   l'unité : la valeur d'une option est `équation:unité`. */
const VARIANTES = (p) => ({ conseil: PLAN_TEMPS(p), formation: PLAN_FORMATION(p) });
/* Les modèles dont le revenu se reconnaît à la livraison, sans étalement : un
   produit est livré, une formation est donnée, le revenu est acquis (2026-09-11). */
const REC_LIVRAISON = ['produit', 'formation'];
/* Les modèles dont le revenu s'ÉTALE toujours, sur un ou plusieurs mois : un
   carnet se consomme après sa vente, un projet avance (2026-09-11). */
const REC_ETALEE = ['carnet', 'projet'];
/* Un projet a une DURÉE (2026-09-11), choisie avec le modèle : le revenu se
   reconnaît dessus, et la livraison tombe au bout. */
const AVEC_DUREE = ['projet'];
const PLAN = (p) => ({
  volume_price: { q: p('en quantités, à un prix unitaire'), saisie: p('des quantités livrées par mois'), prix: p('à un prix moyen'), eq: p('Quantité × prix') },
  time_rate: { q: p('en heures ou en jours, à un tarif'), saisie: p('du temps par mois'), eq: p('Temps × tarif') },
  base_arpu: { q: p('en base installée, à un revenu par période'), saisie: p('une base et un revenu par période'), eq: p('Base × revenu unitaire') },
  capacity_occupancy: { q: p("en capacité, à un taux d'occupation"), saisie: p("un taux d'occupation par mois"), eq: p('Capacité × occupation × tarif') },
  base_rate: { q: p("en pourcentage d'un autre revenu"), saisie: p("un taux sur l'assiette choisie"), eq: p('Assiette × taux') },
  project: { q: p('en valeur'), saisie: p('une valeur par mois'), eq: p('Montant planifié') },
});
/* Les équations qui ont un SENS pour chaque modèle (2026-09-11) : un produit
   se compte ou se tape en montant, du temps se facture au temps ou au forfait,
   un abonnement se lit en base installée. Proposer les six partout donnait
   un conseil « en pourcentage d'un autre revenu ». */
const EQ_PAR_MODELE = {
  produit: ['volume_price', 'project'], volume: ['volume_price'],
  conseil: ['time_rate', 'project'], carnet: ['volume_price'], formation: ['volume_price', 'project'],
  transport: ['volume_price'], forfait: ['volume_price', 'project'], projet: ['project', 'time_rate'],
  services_manages: ['base_arpu', 'project'], abonnement: ['base_arpu'], licence: ['base_arpu'], location: ['base_arpu'],
  espace: ['capacity_occupancy', 'base_arpu'], commission: ['base_rate'],
  autre: ['volume_price', 'time_rate', 'base_arpu', 'capacity_occupancy', 'base_rate', 'project'],
};
/* Les CONDITIONS (2026-09-11) : un seul choix, qui dit quand la facture
   tombe, la livraison saisie servant de repère ; l'encaissement suit la
   facture d'un délai. Ce qui a un sens par équation : ce qu'on compte se
   facture à la livraison, à la commande ou par tranches ; du temps, à la
   livraison ou à la commande (le carnet) ; une base installée, à la commande
   renouvelable ou à terme échu ; une valeur, par tranches d'abord. */
const FAC_PAR_EQ = {
  volume_price: ['on_sale', 'on_delivery', 'tranches'],
  time_rate: ['on_sale', 'on_delivery'],
  base_arpu: ['periodic', 'on_delivery'],
  capacity_occupancy: ['on_delivery', 'periodic'],
  base_rate: ['on_delivery'],
  project: ['on_sale', 'on_delivery', 'tranches'],
};
/* La liste garde TOUJOURS le même ordre, quelle que soit l'équation (2026-09-11) :
   à la commande, à la livraison, par tranches, renouvelable. */
const ORDRE_FAC = ['on_sale', 'on_delivery', 'tranches', 'periodic', 'monthly', 'on_due'];
const FAC_EXCLUS = { formation: ['tranches'] };   // une formation ne se paie pas par tranches
/* Produit, carnet, formation et temps de prestations se planifient en
   COMMANDES, sans alternative (2026-09-11) : on ne planifie pas la livraison.
   Le carnet se paie à la commande et se remet le mois même. */
const FAC_SEULE = { carnet: 'on_sale' };
const COMMANDES_SEULES = ['produit', 'carnet', 'formation', 'conseil', 'projet'];
const SANS_DELAI = ['carnet'];
const SAISIE_MODELE = (p) => ({ carnet: p('des carnets vendus par mois') });
const FAC = (p) => ({ on_delivery: p('à la livraison'), on_sale: p('à la commande'), tranches: p('par tranches'), periodic: p('à la commande, renouvelable'), monthly: p('mensuelle'), on_due: p('au début de chaque période') });
const conditions = (model, eq, fac, produit, base) => Array.from(new Set([...(FAC_PAR_EQ[eq] || ['on_sale', 'on_delivery', 'tranches', 'periodic']), fac].filter(Boolean)))
  .sort((x, y) => ORDRE_FAC.indexOf(x) - ORDRE_FAC.indexOf(y))
  .filter((k) => !(FAC_EXCLUS[model] || []).includes(k))
  .filter((k) => !FAC_SEULE[model] || k === FAC_SEULE[model])
  /* Un produit planifié en LIVRAISONS se facture à la livraison (2026-09-11) :
     sinon le réalisé, livré, et le planifié, commandé, se mélangent. */
  .filter((k) => !(produit && base !== 'order' && k !== 'on_delivery'));
/* Les deux délais, en mois, de 0 à 6 : la livraison après la commande (il ne
   sert qu'à ce qui se facture à la commande) et le paiement après la facture. */
const MOIS_OPTS = (p) => Array.from({ length: 7 }, (_, n) => ({ value: String(n), label: n === 0 ? p('le mois même') : `${n} ${p('mois')}` }));
/* Les TRANCHES d'un paiement (2026-09-11) : un pourcentage et un moment
   chacune. Le défaut est l'acompte classique, 30 % à la commande et 70 % à la
   livraison ; on le voit et on le change ici, il ne restait qu'en base. */
const MOMENTS = (p) => ({ order: p('à la commande'), delivery: p('à la livraison'), m: p('mois après la commande') });
const momentDe = (at) => (at && at.startsWith('m') ? 'm' : at || 'order');
const moisDe = (at) => (at && at.startsWith('m') ? Number(at.slice(1)) : null);
/* L'échéancier se TAPE sans son solde (2026-09-11) : les tranches à la commande
   ou n mois après, puis le solde à la livraison, calculé. Une tranche de plus
   s'insère avant le solde. */
const TRANCHES_DEFAUT = [{ pct: 30, at: 'order' }];
const sansSolde = (t) => (t || []).filter((x) => x.at !== 'delivery');
const avecSolde = (t) => [...t, { pct: Math.round((100 - t.reduce((a, x) => a + (Number(x.pct) || 0), 0)) * 100) / 100, at: 'delivery' }];
const opts = (obj) => Object.entries(obj).map(([value, label]) => ({ value, label }));
const maj = (t) => t.charAt(0).toUpperCase() + t.slice(1);


export default function OfferCreateDialog({ modeles, lines, offre = null, onCreate, onClose }) {
  const heuresJour = HEURES_JOUR;
  const p = useP();
  const C = CHOIX(p);
  const P = PLAN(p);
  const edition = !!offre;
  /* Tous les modèles du catalogue : la coche qui en retirait a disparu (2026-09-12). */
  const dispo = useMemo(() => modeles || [], [modeles]);
  const [model, setModel] = useState(() => (offre && offre.model) || PRINCIPAUX.find((k) => dispo.some((x) => x.model === k)) || (dispo[0] && dispo[0].model) || 'produit');
  const m = useMemo(() => dispo.find((x) => x.model === model) || (modeles || []).find((x) => x.model === model) || {}, [dispo, modeles, model]);
  const [eq, setEq] = useState(offre ? offre.equation || 'volume_price' : 'volume_price');
  const [nom, setNom] = useState(offre ? offre.name || '' : '');
  const [unit, setUnit] = useState(offre ? offre.unit || '' : '');
  const [prix, setPrix] = useState(offre && offre.price != null ? String(offre.price) : '');
  /* Les deux tarifs du temps se tapent chacun dans son champ : ce qu'on tape
     reste tel quel tant qu'on y est (2026-09-12), le prix se convertit derrière.
     Sans ce brouillon, « 1 » devenait 0.13 de l'heure puis 1.04 de la journée,
     et le champ ne se laissait plus remplir. */
  const [brouillonH, setBrouillonH] = useState(null);
  const [brouillonJ, setBrouillonJ] = useState(null);
  const [compte, setCompte] = useState('');
  const [fac, setFac] = useState('');
  const [facN, setFacN] = useState(1);
  const [rec, setRec] = useState(1);
  const [delai, setDelai] = useState(1);
  const [avance, setAvance] = useState(0);
  const [duree, setDuree] = useState(3);              // la durée d'un projet, en mois
  const [base, setBase] = useState('delivery');       // ce que le plan saisit : livraisons, ou commandes
  const [dec, setDec] = useState(false);
  const [tranches, setTranches] = useState(offre && offre.payment_tranches && offre.payment_tranches.length ? sansSolde(offre.payment_tranches) : TRANCHES_DEFAUT);
  const [touche, setTouche] = useState(false);
  const [err, setErr] = useState(null);

  const poserDepuis = (src) => {
    setEq(src.equation || 'volume_price');
    setUnit(src.unit || ''); setCompte(src.revenue_line_id || '');
    setFac(src.billing || 'on_delivery'); setFacN(src.billing_months || 1);
    setRec(src.recognition === 'spread' ? (src.period_months || 1) : 1);
    setDelai(src.payment_months == null ? 1 : src.payment_months); setAvance(src.lead_months || 0); setBase(src.plan_basis || 'delivery');
    setDuree(src.period_months || src.lead_months || 3);
    setDec(!!src.decimals); setTranches(src.payment_tranches && src.payment_tranches.length ? sansSolde(src.payment_tranches) : TRANCHES_DEFAUT); setTouche(false);
  };
  const poser = () => poserDepuis(m);
  const [premier, setPremier] = useState(true);
  useEffect(() => {
    if (edition && premier && offre.model === model) poserDepuis(offre); else poser();
    setPremier(false);
  }, [model]); // eslint-disable-line
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !document.querySelector('.lk-lb__menu')) onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const c = C[model] || C.autre;
  const t = (f) => (v) => { f(v); setTouche(true); };
  const produit = m.family === 'product' && model !== 'autre';
  const etalee = REC_ETALEE.includes(model);
  const avecDuree = AVEC_DUREE.includes(model);
  const recEff = avecDuree ? Math.max(1, Number(duree) || 1) : rec;
  const avanceEff = avecDuree ? Math.max(0, Number(duree) || 0) : avance;
  useEffect(() => {
    /* La base EFFECTIVE d'abord : un modèle « commandes seules » l'est même
       avant que l'état ne l'ait rattrapé, sinon la garde « livraisons →
       facturé à la livraison » écrasait le « à la commande » du modèle. */
    const baseEff = COMMANDES_SEULES.includes(model) ? 'order' : base;
    if (baseEff !== base) setBase('order');
    if ((FAC_EXCLUS[model] || []).includes(fac)) setFac((FAC_PAR_EQ[eq] || ['on_delivery'])[0]);
    if (produit && baseEff !== 'order' && fac !== 'on_delivery') setFac('on_delivery');
    if (FAC_SEULE[model] && fac !== FAC_SEULE[model]) setFac(FAC_SEULE[model]);
  }, [fac, model, base]); // eslint-disable-line
  const avanceTxt = Number(avance) ? `, ${p('commande')} ${avance} ${p('mois avant la livraison')}` : '';
  const facTxt = fac === 'periodic' ? `${p('à la commande, tous les')} ${facN} ${p('mois')}`
    : fac === 'tranches' ? `${p('par tranches')} : ${avecSolde(tranches).map((x) => `${Number(x.pct) || 0} % ${momentDe(x.at) === 'm' ? `${moisDe(x.at)} ${MOMENTS(p).m}` : MOMENTS(p)[momentDe(x.at)]}`).join(', ')}${avanceTxt}`
    : `${FAC(p)[fac] || fac}${fac === 'on_sale' || (fac === 'on_delivery' && base === 'order') ? avanceTxt : ''}`;
  const payTxt = `${p('sur facture')}, ${Number(delai) === 0 ? p('le mois même') : Number(delai) === 1 ? p('le mois suivant') : `${delai} ${p('mois après')}`}`;
  const enCommandes = base === 'order';   // le renouvelable aussi (2026-09-12) : un service préfacturé se vend, puis se rend
  const avecDelaiLivraison = fac === 'on_sale' || fac === 'tranches' || enCommandes;   // le carnet le montre figé à zéro
  const creer = () => {
    if (!nom.trim()) { setErr(p('Donnez un nom à votre offre.')); return; }
    if (fac === 'tranches' && tranches.reduce((a, x) => a + (Number(x.pct) || 0), 0) > 100) { setErr(p('Les tranches dépassent 100 %.')); return; }
    onCreate({ model, name: nom.trim(), unit: unit || undefined, price: prix === '' ? undefined : Number(prix),
      equation: eq, revenue_line_id: compte || null, billing: fac, billing_months: fac === 'periodic' ? Number(facN) : undefined,
      recognition: etalee || recEff > 1 ? 'spread' : 'same_month', period_months: etalee || recEff > 1 ? Math.max(1, Number(recEff) || 1) : null,
      lead_months: avecDelaiLivraison ? Number(avanceEff) : null, payment_months: Number(delai),
      plan_basis: enCommandes ? 'order' : 'delivery',
      payment_tranches: fac === 'tranches' ? avecSolde(tranches) : undefined, decimals: dec });
  };
  const variantes = VARIANTES(p)[model];
  const variante = variantes && (variantes.find((o) => o.value === `${eq}:${unit}`) || variantes.find((o) => o.value.startsWith(`${eq}:`)) || variantes.find((o) => o.value === eq));
  const sansPrix = eq === 'project';
  const sansUnite = eq === 'project' || eq === 'base_rate' || eq === 'time_rate';   // le temps se compte en heures ou en jours-hommes, dits plus haut (2026-09-11)

  return (
    <div className="ocd-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ocd" role="dialog" aria-modal="true" aria-labelledby="ocd-titre">
        <div className="ocd__tt">
          <h3 id="ocd-titre">{edition ? p("Modifier l'offre") : p('Nouvelle offre')}</h3>
          <span className={`ocd__pill${touche ? ' is-on' : ''}`}>{touche ? p('Conditions ajustées') : p('Conditions proposées')}</span>
        </div>
        <div className="ocd__lay">
        <div className="ocd__ed">
        <div className="ocd__sec">
          <h4><span className="ocd__n">1</span>{p('Produit ou prestation')}</h4>
          <div className="ocd__g">
            <label className="ocd__f ocd__f--w">{p('Nom')}
              <input value={nom} autoFocus onChange={(e) => { setNom(e.target.value); setErr(null); }} placeholder={maj(c.quest)} />
            </label>
            <label className="ocd__f ocd__f--w">{p('Modèle')}
              <Listbox value={model} onChange={setModel}
                       options={PRINCIPAUX.filter((k) => dispo.some((x) => x.model === k)).map((k) => ({ value: k, label: maj(C[k].quest) }))} />
            </label>
            {/* Facturé à la commande, on choisit ici ce qu'on planifie, les
                livraisons ou les commandes (2026-09-11) : c'est la même
                question que « comment ». */}
            <label className="ocd__f ocd__f--w">{p('Comment le planifiez-vous ?')}
              <span className="ocd__in">
                {variantes
                  ? <Listbox value={variante ? variante.value : eq} disabled={variantes.length <= 1}
                             onChange={(v) => { const [e, u] = v.split(':'); t(setEq)(e); if (u) setUnit(u); }}
                             options={variantes.map((o) => ({ value: o.value, label: maj(o.label) }))} />
                  : <Listbox value={eq} onChange={t(setEq)} disabled={Array.from(new Set([...(EQ_PAR_MODELE[model] || EQ_PAR_MODELE.autre), eq])).length <= 1}
                             options={Array.from(new Set([...(EQ_PAR_MODELE[model] || EQ_PAR_MODELE.autre), eq])).map((k) => ({ value: k, label: maj(P[k].q) }))} />}
                {/* Un choix forcé reste VISIBLE, en lecture (2026-09-11) : on voit ce
                    qui s'applique, on ne peut pas le changer. */}
                <Listbox value={base} onChange={t(setBase)}
                         disabled={COMMANDES_SEULES.includes(model)}
                         options={[{ value: 'delivery', label: p('les livraisons') }, { value: 'order', label: p('les commandes') }]} />
              </span>
            </label>
            {avecDuree && (
              <label className="ocd__f">{p('Durée (mois)')}
                <input type="number" min="1" value={duree} onChange={(e) => t(setDuree)(e.target.value)} placeholder="…" />
              </label>
            )}
            {/* Du temps se tarife à l'heure ET à la journée (2026-09-11) : les deux
                champs se tiennent par les heures d'une journée de l'entreprise ;
                le prix se garde dans l'unité choisie. */}
            {!sansPrix && eq === 'time_rate' ? (
              <>
                <label className="ocd__f">{p('Tarif horaire (CHF)')}
                  <input type="number"
                         value={brouillonH != null ? brouillonH : unit === 'jh' ? (prix === '' ? '' : String(Math.round((Number(prix) / heuresJour) * 100) / 100)) : prix}
                         onChange={(e) => { const v = e.target.value; setBrouillonH(v); setPrix(v === '' ? '' : unit === 'jh' ? String(Number(v) * heuresJour) : v); }}
                         onBlur={() => setBrouillonH(null)} placeholder="…" />
                </label>
                <label className="ocd__f">{p('Tarif journalier (CHF)')}
                  <input type="number"
                         value={brouillonJ != null ? brouillonJ : unit === 'jh' ? prix : (prix === '' ? '' : String(Math.round(Number(prix) * heuresJour * 100) / 100))}
                         onChange={(e) => { const v = e.target.value; setBrouillonJ(v); setPrix(v === '' ? '' : unit === 'jh' ? v : String(Number(v) / heuresJour)); }}
                         onBlur={() => setBrouillonJ(null)} placeholder="…" />
                </label>
              </>
            ) : !sansPrix && (
              <label className="ocd__f">{eq === 'base_rate' ? p('Taux (%)') : eq === 'base_arpu' ? p('Revenu par période (CHF)') : p('Prix (CHF)')}
                <input type="number" value={prix} onChange={(e) => setPrix(e.target.value)} placeholder="…" />
              </label>
            )}
            {!sansUnite && (
              <label className="ocd__f">{p('Unité')}
                <input value={unit ? libelleUnite(p, unit) : ''} onChange={(e) => setUnit(cleUnite(p, e.target.value))} placeholder={p('pièce')} />
              </label>
            )}
          </div>
        </div>

        {/* Carte 2 : la RECONNAISSANCE du revenu, le compte de chiffre d'affaires
            qui la porte et sur combien de mois elle s'étale (2026-09-11). */}
        <div className="ocd__sec">
          <h4><span className="ocd__n">2</span>{p('Reconnaissance des revenus')}</h4>
          <div className="ocd__g">
            {lines.length > 0 && (
              <label className="ocd__f ocd__f--w">{p('Compte de revenu')}
                <Listbox value={compte} onChange={t(setCompte)} placeholder={p('à attribuer')} className={compte ? '' : 'lk-todo'}
                         options={[{ value: '', label: p('à attribuer') }, ...lines.map((l) => ({ value: l.value, label: l.label }))]} />
              </label>
            )}
            {/* À la livraison, ou étalé : le nombre de mois n'apparaît que dans le
                second cas. */}
            <label className="ocd__f ocd__f--w">{p('Reconnaissance')}
              <span className="ocd__in ocd__in--fixe">
                <Listbox value={etalee || recEff > 1 ? 'spread' : 'same'} onChange={(v) => t(setRec)(v === 'spread' ? Math.max(2, Number(rec) || 2) : 1)}
                         disabled={REC_LIVRAISON.includes(model) || etalee}
                         options={[{ value: 'same', label: p('à la livraison') }, ...(REC_LIVRAISON.includes(model) ? [] : [{ value: 'spread', label: p('sur plusieurs mois') }])]} />
                {(etalee || recEff > 1) && <><input type="number" min={etalee ? 1 : 2} value={recEff} disabled={avecDuree} onChange={(e) => t(setRec)(e.target.value)} className="ocd__n" /><span>{p('mois')}</span></>}
              </span>
            </label>
          </div>
        </div>

        <div className="ocd__sec">
          <h4><span className="ocd__n">3</span>{p('Conditions habituelles')}<button type="button" className="ocd__txt" onClick={poser}>{p('Par défaut')}</button></h4>
          <div className="ocd__g">
            {/* Un seul choix : quand on facture. Le renouvelable demande sa période ;
                puis deux délais, la livraison après la commande (seulement pour ce
                qui se facture à la commande) et le paiement après la facture. */}
            <label className="ocd__f">{p('Facturation')}
              <span className="ocd__in">
                <Listbox value={fac} onChange={t(setFac)} disabled={conditions(model, eq, fac, produit, base).length <= 1}
                         options={conditions(model, eq, fac, produit, base).map((k) => ({ value: k, label: FAC(p)[k] || k }))} />
                {fac === 'periodic' && <><span>{p('tous les')}</span><input type="number" min="1" value={facN} onChange={(e) => t(setFacN)(e.target.value)} className="ocd__n" /><span>{p('mois')}</span></>}
              </span>
            </label>
            <label className="ocd__f">{p('Délai de paiement')}
              <Listbox value={String(delai)} onChange={(v) => t(setDelai)(Number(v))} options={MOIS_OPTS(p)} />
            </label>
            {avecDelaiLivraison && (
              <label className="ocd__f">{p('Délai de livraison')}
                <Listbox value={SANS_DELAI.includes(model) ? '0' : String(avanceEff)} onChange={(v) => t(setAvance)(Number(v))}
                         disabled={SANS_DELAI.includes(model) || avecDuree}
                         options={[...MOIS_OPTS(p), ...(avanceEff > 6 ? [{ value: String(avanceEff), label: `${avanceEff} ${p('mois')}` }] : [])]} />
              </label>
            )}
            {fac === 'tranches' && (() => {
              const total = tranches.reduce((acc, x) => acc + (Number(x.pct) || 0), 0);
              const solde = Math.round((100 - total) * 100) / 100;
              const poserT = (i, patch) => { setTranches(tranches.map((x, j) => (j === i ? { ...x, ...patch } : x))); setTouche(true); };
              const moments = { order: MOMENTS(p).order, m: MOMENTS(p).m };
              return (
                <div className="ocd__f ocd__f--w ocd__tr">
                  <span>{p('Échéancier')}</span>
                  {tranches.map((x, i) => (
                    <div className="ocd__tr-l" key={i}>
                      <input type="number" min="0" max="100" value={x.pct} onChange={(e) => poserT(i, { pct: Number(e.target.value) })} className="ocd__n" />
                      <span>%</span>
                      <Listbox value={momentDe(x.at)} onChange={(v) => poserT(i, { at: v === 'm' ? `m${moisDe(x.at) || 1}` : v })} options={opts(moments)} />
                      {momentDe(x.at) === 'm' && <><input type="number" min="1" value={moisDe(x.at) || 1} onChange={(e) => poserT(i, { at: `m${e.target.value || 1}` })} className="ocd__n" /><span>{p('mois')}</span></>}
                      <button type="button" className="ocd__x" title={p('Supprimer')} onClick={() => { setTranches(tranches.filter((_, j) => j !== i)); setTouche(true); }}>✕</button>
                    </div>
                  ))}
                  {/* Le SOLDE à la livraison se calcule : il se lit, il ne se tape pas. */}
                  <div className="ocd__tr-l ocd__tr-l--solde">
                    <span className={`ocd__n ocd__solde${solde < 0 ? ' is-neg' : ''}`}>{solde}</span>
                    <span>%</span>
                    <span className="ocd__solde-lbl">{p('à la livraison')} · {p('solde')}</span>
                  </div>
                  <div className="ocd__tr-f">
                    <button type="button" className="ocd__txt" onClick={() => { setTranches([...tranches, { pct: 10, at: 'm1' }]); setTouche(true); }}>＋ {p('tranche')}</button>
                    {solde < 0 && <span className="ocd__reste">{p('les tranches dépassent 100 %')}</span>}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {err && <div className="ocd__err">{err}</div>}
        </div>
        {/* La SYNTHÈSE, à droite, en phrases : ce que le plan demandera. Elle se
            lit, elle ne se tape pas. */}
        <aside className="ocd__sum">
          <div className="ocd__eb">{p('Votre offre est paramétrée ainsi')}</div>
          <h4><OfferIcon name={iconOf({ model })} size={18} /> {nom.trim() || (edition ? offre.name : p('Nouvelle offre'))}</h4>
          <div className="ocd__r"><span>{p('Dans le plan, vous saisirez')}</span>
            <b>{maj(SAISIE_MODELE(p)[model] || (variante && variante.saisie) || (enCommandes && eq === 'volume_price' ? p('des quantités commandées par mois') : enCommandes && eq === 'project' ? p('une valeur commandée par mois') : P[eq].saisie))}{P[eq].prix && <><br />{P[eq].prix}</>}</b></div>
          <div className="ocd__r"><span>{p("Le chiffre d'affaires se calcule")}</span><b>{P[eq].eq}{prix !== '' && !sansPrix ? ` · ${formatNumber(Number(prix), 0)} ${eq === 'base_rate' ? '%' : 'CHF'}` : ''}</b></div>
          <div className="ocd__r"><span>{p('Mode')}</span><b>{c.mode}</b></div>
          <div className="ocd__r"><span>{p('Facturation')}</span><b>{maj(facTxt)}</b></div>
          {/* La RECONNAISSANCE se lit en cellules : une par mois, la première marquée. */}
          <div className="ocd__r">
            <span>{p('Reconnaissance des revenus')}</span>
            <b>{rec > 1 ? `${p('Sur')} ${rec} ${p('mois')}` : p('Sur le mois')}</b>
            <span className="ocd__cells" aria-hidden="true">
              {Array.from({ length: Math.min(Number(rec) || 1, 24) }, (_, i) => <i key={i} className={i === 0 ? 'is-first' : ''} />)}
            </span>
          </div>
          <div className="ocd__r"><span>{p('Encaissement')}</span><b>{maj(payTxt)}</b></div>
          {/* Les QUANTITÉS se lisent ici et se changent d'un clic (2026-09-11) : un
              réglage rare n'a pas sa place parmi les conditions. */}
          {!sansUnite && (
            <div className="ocd__r">
              <span>{p('Quantités')}</span>
              <button type="button" className="ocd__sw" onClick={() => t(setDec)(!dec)} title={p('Cliquer pour changer')}>
                {dec ? p('décimales') : p('entières')}<span aria-hidden="true"> ⇄</span>
              </button>
            </div>
          )}
          <p className="ocd__sf">{p('Aucun calendrier à saisir ici.')}<br />{p('Les mois viendront de votre plan.')}</p>
          {/* Un lien de courriel, adressé à Olivier, avec l'objet déjà posé. */}
          <a className="ocd__cta" href={`mailto:olivier@myfp.ch?subject=${encodeURIComponent(p("Palestre : demande d'un nouveau modèle d'offre"))}`}>
            {p('Vous souhaiteriez un autre modèle ? Écrivez-nous !')}
          </a>
        </aside>
        </div>
        <div className="ocd__act">
          <small>{touche ? p('Conditions ajustées pour cette offre.') : p('Conditions habituelles proposées pour ce modèle.')}</small>
          <span>
            <button type="button" className="of-btn" onClick={onClose}>{p('Annuler')}</button>
            <button type="button" className="ocd__pri" onClick={creer}>{edition ? p('Enregistrer') : p("Créer l'offre")}</button>
          </span>
        </div>
      </div>
    </div>
  );
}
